import Navbar from '../components/Navbar';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-[#F2F7F4] texture-grain">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-[#2D6A4F] text-sm font-medium mb-6 hover:underline">
          <ArrowLeft size={14} /> Back
        </Link>

        <div className="bg-white rounded-[2rem] p-8 sm:p-12 shadow-[0_8px_30px_rgb(27,67,50,0.08)]" data-testid="terms-page">
          <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[#1B4332] mb-2">Terms of Service</h1>
          <p className="text-sm text-[#4B6B58] mb-8">Last updated: March 24, 2026</p>

          <div className="prose prose-sm max-w-none text-[#122A1F] space-y-6">
            <section>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">1. Agreement to Terms</h2>
              <p className="text-sm leading-relaxed text-[#4B6B58]">
                By accessing or using Stable Solutions by The Oaks Legacy Ranch ("the Service"), available at www.stablesolutions-theoakslegacyranch.com, you agree to be bound by these Terms of Service. The Service is operated by The New Initiative Community Enhancement Foundation (The N.I.C.E. Foundation), a domestic nonprofit corporation registered in the State of Oregon (Registry Number: 252577391, EIN: 41-4838456), doing business as The Oaks Legacy Ranch, located in Yamhill, OR 97148. If you do not agree to these terms, please do not use the Service.
              </p>
            </section>

            <section>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">2. Description of Service</h2>
              <p className="text-sm leading-relaxed text-[#4B6B58]">
                Stable Solutions is a subscription-based equine health record management platform. The Service provides tools for tracking horse health records, vaccinations, veterinary visits, medications, farrier visits, training, expenses, breeding, and show records, along with additional features including AI-powered health insights, a community marketplace, event discovery, barn management tools, and subscriber chat.
              </p>
            </section>

            <section>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">3. Account Registration</h2>
              <p className="text-sm leading-relaxed text-[#4B6B58]">
                To use the Service, you must create an account by providing accurate and complete information. You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account. You must be at least 18 years of age to create an account. You agree to notify us immediately of any unauthorized use of your account.
              </p>
            </section>

            <section>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">4. Free Trial & Subscription</h2>
              <p className="text-sm leading-relaxed text-[#4B6B58]">
                New users receive a complimentary 30-day free trial with full access to all features. No credit card is required for the trial period. After the trial period:
              </p>
              <ul className="list-disc list-inside text-sm text-[#4B6B58] mt-2 space-y-1">
                <li><strong>Early Bird Rate:</strong> $9.95 per month for users who subscribe before their trial expires, locked in for the first 12 months of subscription.</li>
                <li><strong>Standard Rate:</strong> $14.95 per month for users who subscribe after their trial expires, or after the first year of an early bird subscription.</li>
              </ul>
              <p className="text-sm leading-relaxed text-[#4B6B58] mt-2">
                If you do not subscribe after your trial ends, your account will revert to read-only access. Your data will be preserved but you will not be able to add, edit, or delete records until you subscribe. Subscription payments are processed securely through Stripe. All revenue generated supports the programs and mission of The N.I.C.E. Foundation.
              </p>
            </section>

            <section>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">5. Cancellation & Refunds</h2>
              <p className="text-sm leading-relaxed text-[#4B6B58]">
                You may cancel your subscription at any time. Upon cancellation, you will retain access until the end of your current billing period. After that, your account will revert to read-only access. We do not provide refunds for partial months. Your data will remain accessible in read-only mode after cancellation.
              </p>
            </section>

            <section>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">6. User Content & Data Ownership</h2>
              <p className="text-sm leading-relaxed text-[#4B6B58]">
                You retain ownership of all content and data you upload to the Service, including but not limited to horse records, photos, documents, and any information entered into the platform. By using the Service, you grant us a limited license to store, process, and display your content solely for the purpose of providing the Service. We do not sell, share, or use your data for any purpose other than operating the Service.
              </p>
            </section>

            <section>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">7. Horse Card Sharing</h2>
              <p className="text-sm leading-relaxed text-[#4B6B58]">
                The Service allows you to share horse records with other registered users via share links. When you share a horse card, the recipient can view your horse's records and add new records. You remain the owner of the horse card and can revoke access at any time. You are responsible for managing who has access to your horse's information.
              </p>
            </section>

            <section>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">8. Community Features</h2>
              <p className="text-sm leading-relaxed text-[#4B6B58]">
                The Service includes community features such as chat, a marketplace, and an events board. You agree to use these features responsibly and not to post content that is unlawful, harmful, threatening, abusive, harassing, defamatory, or otherwise objectionable. We reserve the right to remove any content that violates these terms. Marketplace transactions are conducted between users; The N.I.C.E. Foundation is not a party to these transactions and assumes no liability for them.
              </p>
            </section>

            <section>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">9. AI-Powered Features</h2>
              <p className="text-sm leading-relaxed text-[#4B6B58]">
                The Service includes AI-powered features such as health insights and event discovery. These features are provided for informational purposes only and should not be considered a substitute for professional veterinary advice, diagnosis, or treatment. Always consult a qualified veterinarian for your horse's health needs.
              </p>
            </section>

            <section>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">10. Limitation of Liability</h2>
              <p className="text-sm leading-relaxed text-[#4B6B58]">
                The Service is provided "as is" and "as available" without warranties of any kind. The N.I.C.E. Foundation shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service. Our total liability shall not exceed the amount you paid for the Service in the 12 months preceding the claim.
              </p>
            </section>

            <section>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">11. Modifications to Terms</h2>
              <p className="text-sm leading-relaxed text-[#4B6B58]">
                We reserve the right to modify these Terms at any time. Material changes will be communicated via the Service or email. Your continued use of the Service after changes constitutes acceptance of the modified Terms.
              </p>
            </section>

            <section>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">12. Governing Law</h2>
              <p className="text-sm leading-relaxed text-[#4B6B58]">
                These Terms shall be governed by and construed in accordance with the laws of the State of Oregon, without regard to its conflict of law provisions. Any disputes arising from these Terms or the Service shall be resolved in the courts of Yamhill County, Oregon.
              </p>
            </section>

            <section>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">13. Contact Information</h2>
              <p className="text-sm leading-relaxed text-[#4B6B58]">
                For questions about these Terms of Service, please contact:<br /><br />
                <strong>The New Initiative Community Enhancement Foundation</strong><br />
                d/b/a The Oaks Legacy Ranch<br />
                Yamhill, OR 97148<br />
                EIN: 41-4838456<br />
                Oregon Registry: 252577391
              </p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
